import React from 'react';
import { X, MapPin, Phone, MessageCircle, Mail, Sparkles, Bed, Maximize2, Compass, Calendar, Heart, Share2, ArrowRight } from 'lucide-react';

/**
 * PropertyDetailModal — görsel 22 stiliyle
 * Sol: büyük property görseli + thumbnails
 * Sağ: AI eşleşme + danışman + özet + DEO-Bot mesajı
 */
export default function PropertyDetailModal({ property, onClose }) {
  if (!property) return null;

  const score = 92;
  const consultant = {
    name: 'Mehmet K.',
    role: 'Kıdemli Lüks Portföy Danışmanı',
    phone: '+90 (242) 000 00 00',
    listed: 23,
    sold: 142,
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6"
      style={{ background: 'rgba(11, 7, 3, 0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="property-detail-modal"
    >
      <div
        className="relative w-full max-w-[1200px] max-h-[92vh] rounded-3xl overflow-y-auto lg:overflow-hidden grid lg:grid-cols-12"
        style={{
          background: 'linear-gradient(135deg, #2B1E16 0%, #1C130D 100%)',
          border: '1px solid rgba(197, 108, 57, 0.45)',
          boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 40px 80px -16px rgba(0,0,0,0.6)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-30 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{
            background: 'rgba(15,8,4,0.85)',
            border: '1px solid rgba(197,108,57,0.45)',
            backdropFilter: 'blur(10px)',
          }}
          data-testid="modal-close"
        >
          <X size={16} className="text-[#E08A4F]" />
        </button>

        {/* LEFT — gallery */}
        <div className="lg:col-span-7 relative" style={{ minHeight: '320px' }}>
          <img
            src={property.image}
            alt={property.title}
            className="w-full h-full object-cover absolute inset-0"
            style={{ filter: 'saturate(1.08) contrast(1.04)' }}
          />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(180deg, rgba(11,7,3,0.55) 0%, transparent 25%, transparent 70%, rgba(11,7,3,0.85) 100%)',
          }}/>

          {/* Top labels */}
          <div className="absolute top-5 left-5 flex items-center gap-2">
            <span className="capsule capsule-copper backdrop-blur-md">{property.type}</span>
            <span className="capsule glass text-[#F4ECDF] backdrop-blur-md">{property.code}</span>
          </div>

          {/* Actions */}
          <div className="absolute top-5 right-20 flex items-center gap-2">
            <button className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(15,8,4,0.85)', border: '1px solid rgba(197,108,57,0.45)', backdropFilter: 'blur(10px)' }}
              data-testid="modal-favorite"
            >
              <Heart size={14} className="text-[#E08A4F]" />
            </button>
            <button className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(15,8,4,0.85)', border: '1px solid rgba(197,108,57,0.45)', backdropFilter: 'blur(10px)' }}
              data-testid="modal-share"
            >
              <Share2 size={14} className="text-[#E08A4F]" />
            </button>
          </div>

          {/* Bottom info */}
          <div className="absolute bottom-0 inset-x-0 p-6">
            <div className="flex items-center gap-2 mb-2 text-[#F4ECDF]/65 font-body text-[11px] tracking-[0.18em] uppercase">
              <MapPin size={11} /> {property.location}
            </div>
            <h2 className="font-display text-[28px] text-[#F4ECDF] font-light leading-tight tracking-tight mb-3">
              {property.title}
            </h2>
            <div className="flex items-baseline gap-3">
              <div className="font-display text-[36px] copper-shine font-bold tracking-tight">{property.price}</div>
              <div className="font-body text-[12px] text-[#F4ECDF]/55">peşin · pazarlık payı var</div>
            </div>
          </div>

          {/* Thumbs */}
          <div className="absolute bottom-5 right-5 flex gap-2">
            {[1,2,3,4].map(i => (
              <div key={i}
                className="w-12 h-12 rounded-lg overflow-hidden cursor-pointer transition-all hover:scale-105"
                style={{
                  background: `url(${property.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: ['top','center','bottom','right'][i-1] + ' center',
                  border: i === 1 ? '1.5px solid #E08A4F' : '1px solid rgba(244,213,184,0.3)',
                  opacity: i === 1 ? 1 : 0.65,
                }}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — info panel */}
        <div className="lg:col-span-5 p-4 sm:p-6 lg:overflow-y-auto" style={{ maxHeight: 'min(92vh, 100%)' }}>
          {/* AI Match */}
          <div className="rounded-2xl p-5 mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(104,229,178,0.10), rgba(104,229,178,0.04))',
              border: '1px solid rgba(104,229,178,0.40)',
            }}
            data-testid="modal-ai-match"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#68E5B2] font-semibold flex items-center gap-2">
                <Sparkles size={11} /> AI Eşleşme
              </div>
              <div className="font-display text-[24px] text-[#68E5B2] font-bold">%{score}</div>
            </div>
            <div className="font-display text-[13px] text-[#F4ECDF] leading-snug font-light italic">
              "Müşteriniz <span className="font-semibold text-[#68E5B2]">Ayşe T.</span>'nin lüks deniz manzaralı 4+1 talebine birebir uyuyor."
            </div>
          </div>

          {/* Quick specs */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { icon: Bed, val: '4+1', lbl: 'Oda' },
              { icon: Maximize2, val: '285m²', lbl: 'Brüt' },
              { icon: Compass, val: 'Güney', lbl: 'Cephe' },
              { icon: Calendar, val: '2023', lbl: 'Yıl' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-xl p-3 text-center"
                  style={{
                    background: 'rgba(244,213,184,0.06)',
                    border: '1px solid rgba(197,108,57,0.25)',
                  }}
                >
                  <Icon size={14} className="text-[#E08A4F] mx-auto mb-1.5" />
                  <div className="font-display text-[14px] text-[#F4ECDF] font-semibold">{s.val}</div>
                  <div className="font-body text-[9px] uppercase tracking-[0.18em] text-[#F4ECDF]/55 mt-0.5">{s.lbl}</div>
                </div>
              );
            })}
          </div>

          {/* AI Highlights */}
          <div className="rounded-2xl p-4 mb-4"
            style={{
              background: 'rgba(244,213,184,0.04)',
              border: '1px solid rgba(197,108,57,0.30)',
            }}
          >
            <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#E08A4F] font-semibold mb-3 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#68E5B2] animate-pulse" />
              AI Öne Çıkanlar
            </div>
            <div className="flex flex-wrap gap-2">
              {(property.highlights || ['Panoramik Deniz Manzarası', 'Akıllı Ev Sistemi', 'Özel İskele', 'Marina 200m']).map((h, i) => (
                <span key={i} className="capsule capsule-copper text-[10px]" style={{ padding: '5px 10px' }}>{h}</span>
              ))}
            </div>
          </div>

          {/* Consultant */}
          <div className="rounded-2xl p-4 mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(15,8,4,0.92), rgba(28,19,13,0.85))',
              border: '1px solid rgba(197,108,57,0.40)',
            }}
            data-testid="modal-consultant"
          >
            <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#E08A4F] font-semibold mb-3">
              Danışman
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-[#1C130D] flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #E08A4F, #C56C39)', boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset' }}
              >
                MK
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-[14px] text-[#F4ECDF] font-semibold tracking-tight">{consultant.name}</div>
                <div className="font-body text-[10.5px] text-[#F4ECDF]/55 leading-tight">{consultant.role}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/8">
              <div className="flex-1 text-center">
                <div className="font-display text-[16px] text-[#F4ECDF] font-bold">{consultant.listed}</div>
                <div className="font-body text-[9px] uppercase tracking-[0.18em] text-[#F4ECDF]/45">Aktif</div>
              </div>
              <div className="w-px h-8 bg-white/10"/>
              <div className="flex-1 text-center">
                <div className="font-display text-[16px] copper-shine font-bold">{consultant.sold}</div>
                <div className="font-body text-[9px] uppercase tracking-[0.18em] text-[#F4ECDF]/45">Satılan</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button className="rounded-lg py-2 flex items-center justify-center gap-1.5 transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg,#E08A4F,#C56C39)', color: '#1C130D', boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset' }}
                data-testid="modal-call"
              >
                <Phone size={11}/>
                <span className="font-display text-[10px] font-semibold tracking-[0.12em]">ARA</span>
              </button>
              <button className="rounded-lg py-2 flex items-center justify-center gap-1.5"
                style={{ background: 'rgba(104,229,178,0.18)', border: '1px solid rgba(104,229,178,0.45)', color: '#68E5B2' }}
                data-testid="modal-whatsapp"
              >
                <MessageCircle size={11}/>
                <span className="font-display text-[10px] font-semibold tracking-[0.12em]">WP</span>
              </button>
              <button className="rounded-lg py-2 flex items-center justify-center gap-1.5"
                style={{ background: 'rgba(244,213,184,0.08)', border: '1px solid rgba(244,213,184,0.20)', color: '#F4ECDF' }}
                data-testid="modal-mail"
              >
                <Mail size={11}/>
                <span className="font-display text-[10px] font-semibold tracking-[0.12em]">MAIL</span>
              </button>
            </div>
          </div>

          {/* Teknik Detaylar — ısıtma, bina yaşı, krediye uygunluk, vb. */}
          {property.extras && (
            <div className="rounded-2xl p-4 mb-4"
              style={{
                background: 'rgba(244,213,184,0.04)',
                border: '1px solid rgba(197,108,57,0.30)',
              }}
              data-testid="modal-extras"
            >
              <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#E08A4F] font-semibold mb-3">
                Teknik Detaylar
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-3">
                {[
                  ['Isıtma', property.extras.heating],
                  ['Bina Yaşı', `${property.extras.buildingAge} yıl`],
                  ['Kat', property.extras.floor],
                  ['Banyo', property.extras.bathroom],
                  ['Balkon', property.extras.balcony],
                  ['Otopark', property.extras.parking],
                  ['Eşya', property.extras.furniture],
                  ['Cephe', property.extras.facade],
                  ['Tapu', property.extras.deedStatus],
                  ['Kullanım', property.extras.usage],
                  ['Takas', property.extras.swap],
                  ['Yapı', property.extras.buildingType],
                  ['Site', property.extras.site],
                  ['Aidat', property.extras.dues],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex flex-col">
                    <span className="font-body text-[8.5px] tracking-[0.20em] uppercase text-[#F4ECDF]/45 font-semibold">{k}</span>
                    <span className="font-display text-[11.5px] text-[#F4ECDF]/95 leading-snug">{v}</span>
                  </div>
                ))}
              </div>

              {/* Krediye uygunluk şeridi */}
              <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/8">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#68E5B2', boxShadow: '0 0 6px #68E5B2' }} />
                  <span className="font-display text-[11px] text-[#68E5B2] font-semibold tracking-tight">
                    {property.extras.creditEligible}
                  </span>
                </div>
              </div>

              {/* Özellikler — chip listesi */}
              {property.extras.features && property.extras.features.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/8">
                  <div className="font-body text-[8.5px] tracking-[0.22em] uppercase text-[#F4ECDF]/45 font-semibold mb-2">
                    Özellikler
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {property.extras.features.map((f, i) => (
                      <span key={i} className="capsule capsule-copper text-[9px]" style={{ padding: '4px 9px' }}>
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Açıklama */}
              {property.extras.description && (
                <div className="mt-3 pt-3 border-t border-white/8">
                  <div className="font-body text-[8.5px] tracking-[0.22em] uppercase text-[#F4ECDF]/45 font-semibold mb-1.5">
                    Açıklama
                  </div>
                  <p className="font-body text-[11.5px] text-[#F4ECDF]/85 leading-[1.6]">
                    {property.extras.description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* DEO-Bot mesajı */}
          <div className="rounded-2xl p-4 relative"
            style={{
              background: 'linear-gradient(135deg, rgba(224,138,79,0.16), rgba(197,108,57,0.06))',
              border: '1px solid rgba(224,138,79,0.45)',
            }}
            data-testid="modal-bot-message"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, #E08A4F, #8E4A23)',
                  boxShadow: '0 0 14px rgba(224,138,79,0.6), inset 0 1px 0 rgba(244,213,184,0.4)',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#68E5B2]" style={{ boxShadow: '0 0 6px #68E5B2', position:'absolute', transform:'translate(-7px,-1px)' }}/>
                <span className="w-1.5 h-1.5 rounded-full bg-[#F7D002]" style={{ boxShadow: '0 0 6px #F7D002', position:'absolute', transform:'translate(7px,-1px)' }}/>
              </div>
              <div className="flex-1">
                <div className="font-body text-[9px] tracking-[0.22em] uppercase text-[#E08A4F] font-semibold mb-1">DEO-Bot</div>
                <div className="font-display text-[12.5px] text-[#F4ECDF] leading-[1.5] font-light">
                  Ayşe T. için bu portföyün özelleştirilmiş <span className="copper-shine font-semibold">sunumunu</span> hazırlayayım mı?
                </div>
                <button className="mt-2 inline-flex items-center gap-1 text-[#68E5B2] font-body text-[10px] uppercase tracking-[0.18em] font-semibold hover:translate-x-0.5 transition-transform">
                  Sunumu Oluştur <ArrowRight size={11} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
